import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ClipboardCheck,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { Logo } from '../../components/Logo';
import { SearchableDropdown } from '../../components/SearchableDropdown';
import { UserMenu } from '../../components/UserMenu';
import { useViewModelReady } from '../../hooks/useViewModel';
import { CreatedHarborRunnerToken, HarborRunnerToken } from '../../services/harborRunnerTokens';
import { useSettingsViewModel } from './viewModel';
import { paths } from '../../routes/paths';

export function SettingsPage() {
  const navigate = useNavigate();
  const vm = useSettingsViewModel();
  useViewModelReady(vm.initialize);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-7 w-7 text-primary" />
            <Logo className="text-xl font-bold text-gray-900" />
            <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">
              Settings
            </span>
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate(paths.queue())}
              className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to queue
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="flex-1 p-8">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage your profile and notification preferences.
          </p>

          {vm.error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
              {vm.error}
            </div>
          )}

          {vm.busy && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {!vm.busy && (
            <div className="mt-6 space-y-6">
              <section className="bg-white border border-gray-200 rounded-lg">
                <header className="px-4 py-3 border-b border-gray-100">
                  <h2 className="text-sm font-semibold text-gray-900">Profile</h2>
                </header>
                <dl className="divide-y divide-gray-100 text-sm">
                  <ProfileRow label="Name" value={vm.displayName || '—'} />
                  <ProfileRow label="Email" value={vm.email || '—'} />
                  <ProfileRow
                    label="Expert ID"
                    value={vm.expertId || 'Not linked'}
                    muted={!vm.expertId}
                  />
                </dl>
              </section>

              <section className="bg-white border border-gray-200 rounded-lg">
                <header className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900">Notifications</h2>
                  {vm.saving && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
                </header>
                <div className="divide-y divide-gray-100">
                  <NotificationToggle
                    label="Notify me when my work is approved"
                    checked={vm.notifyOnApproved}
                    disabled={vm.saving}
                    onChange={(v) => void vm.setNotifyOnApproved(v)}
                  />
                  <NotificationToggle
                    label="Notify me when my work needs revisions"
                    checked={vm.notifyOnChangesRequested}
                    disabled={vm.saving}
                    onChange={(v) => void vm.setNotifyOnChangesRequested(v)}
                  />
                </div>
              </section>

              <AccessTokenSection
                tokens={vm.accessTokens}
                tokensLoading={vm.accessTokensLoading}
                creating={vm.accessTokenCreating}
                createdToken={vm.accessTokenCreated}
                revokingTokenId={vm.revokingAccessTokenId}
                onCreate={vm.createAccessToken}
                onRevoke={vm.revokeAccessToken}
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

interface AccessTokenSectionProps {
  tokens: HarborRunnerToken[];
  tokensLoading: boolean;
  creating: boolean;
  createdToken: CreatedHarborRunnerToken | null;
  revokingTokenId: string | null;
  onCreate: (label: string, expiresInDays: number) => Promise<void>;
  onRevoke: (tokenId: string) => Promise<void>;
}

function AccessTokenSection({
  tokens,
  tokensLoading,
  creating,
  createdToken,
  revokingTokenId,
  onCreate,
  onRevoke,
}: AccessTokenSectionProps) {
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCopied(false);
    setCopyError(false);
    await onCreate(label, expiresInDays);
  }

  async function copyToken() {
    if (!createdToken) return;
    setCopyError(false);
    if (!navigator.clipboard) {
      setCopyError(true);
      return;
    }
    try {
      await navigator.clipboard.writeText(createdToken.token);
      setCopied(true);
    } catch {
      setCopyError(true);
    }
  }

  function confirmRevoke(token: HarborRunnerToken) {
    if (token.status !== 'active' || revokingTokenId) return;
    if (window.confirm(`Revoke the access token "${token.label}"? Existing runner sessions using it will stop working.`)) {
      void onRevoke(token.id);
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-lg" aria-labelledby="access-token-heading">
      <header className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-primary" aria-hidden="true" />
        <h2 id="access-token-heading" className="text-sm font-semibold text-gray-900">
          Access token
        </h2>
      </header>

      <div className="px-4 py-4 space-y-4">
        <p className="text-sm text-gray-600">
          Create a scoped token for <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">harbor_runner</code>.
          It lets the runner submit and monitor Harbor runs as your account.
        </p>

        <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
          <p>
            Treat this like a password. The full token is shown only once after creation.
          </p>
        </div>

        <form onSubmit={(event) => void handleCreate(event)} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <label className="block text-sm text-gray-700">
              Label
              <input
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="Harbor runner"
                maxLength={80}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </label>
            <SearchableDropdown
              id="expires-in-days"
              label="Expires in"
              options={[
                { value: '30', label: '30 days' },
                { value: '90', label: '90 days' },
                { value: '365', label: '1 year' },
              ]}
              value={String(expiresInDays)}
              onChange={(value) => {
                if (value !== null) setExpiresInDays(Number(value));
              }}
              searchPlaceholder="Search expiration periods"
              preserveOrder
              triggerClassName="mt-1"
            />
          </div>
          <button
            type="submit"
            disabled={creating}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {creating ? 'Creating…' : 'Create access token'}
          </button>
        </form>

        {createdToken && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3" role="status">
            <p className="text-sm font-medium text-green-900">Access token created</p>
            <p className="mt-1 text-xs text-green-800">
              Copy it now. For the runner, set it as <code>WORKBENCH_RUNNER_TOKEN</code>.
            </p>
            <div className="mt-2 flex gap-2">
              <input
                readOnly
                value={createdToken.token}
                aria-label="New access token"
                onFocus={(event) => event.currentTarget.select()}
                className="min-w-0 flex-1 rounded-md border border-green-300 bg-white px-3 py-2 font-mono text-xs text-gray-800"
              />
              <button
                type="button"
                onClick={() => void copyToken()}
                className="inline-flex shrink-0 items-center gap-1 rounded-md border border-green-300 bg-white px-3 py-2 text-sm text-green-800 hover:bg-green-100"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            {copyError && (
              <p className="mt-2 text-xs text-green-800">
                Clipboard access is unavailable. Select the token above and copy it manually.
              </p>
            )}
          </div>
        )}

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Issued tokens</h3>
          {tokensLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading tokens…
            </div>
          ) : tokens.length === 0 ? (
            <p className="py-4 text-sm text-gray-500">No access tokens have been created.</p>
          ) : (
            <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
              {tokens.map((token) => (
                <AccessTokenRow
                  key={token.id}
                  token={token}
                  revoking={revokingTokenId === token.id}
                  onRevoke={() => confirmRevoke(token)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

interface AccessTokenRowProps {
  token: HarborRunnerToken;
  revoking: boolean;
  onRevoke: () => void;
}

function AccessTokenRow({ token, revoking, onRevoke }: AccessTokenRowProps) {
  const statusLabel = token.status === 'active'
    ? 'Active'
    : token.status === 'expired' ? 'Expired' : 'Revoked';
  const statusClass = token.status === 'active'
    ? 'bg-green-50 text-green-700'
    : 'bg-gray-100 text-gray-500';

  return (
    <li className="flex items-center justify-between gap-3 px-3 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-gray-900">{token.label}</p>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${statusClass}`}>
            {statusLabel}
          </span>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          <span className="font-mono">••••{token.lastFour || '••••'}</span>
          {' · '}
          Created {formatDate(token.createdAt)}
          {' · '}
          {token.status === 'expired' ? 'Expired' : 'Expires'} {formatDate(token.expiresAt)}
        </p>
      </div>
      {token.status === 'active' && (
        <button
          type="button"
          onClick={onRevoke}
          disabled={revoking}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-600 hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {revoking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
          Revoke
        </button>
      )}
    </li>
  );
}

function formatDate(value: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}

interface ProfileRowProps {
  label: string;
  value: string;
  muted?: boolean;
}

function ProfileRow({ label, value, muted }: ProfileRowProps) {
  return (
    <div className="px-4 py-3 flex items-center justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`text-right ${muted ? 'text-gray-400' : 'text-gray-900'}`}>{value}</dd>
    </div>
  );
}

interface NotificationToggleProps {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}

function NotificationToggle({ label, checked, disabled, onChange }: NotificationToggleProps) {
  return (
    <label
      className={`px-4 py-3 flex items-center justify-between gap-4 ${
        disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
      }`}
    >
      <span className="text-sm text-gray-900">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
      />
    </label>
  );
}
